import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Activity, ActivityKind, ActivitySearchOptions, Attachment, ItemDeletedResponse, ShareableEntity, getFileThumbnailUrl } from '@jms/model';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { IActivityService } from '../model';
import * as Enumerable from 'linq';


@Injectable({ providedIn: 'root' })
export class ActivityService implements IActivityService {

  public readonly $activities = signal<Activity[]>([]);
  public readonly $updatedActivity = signal<Activity>(null);
  public readonly $activityDeleted = signal<string>(null);
  public readonly $activityAdded = signal<Activity[]>(null);

  protected api = '/api/activities/pro';
  protected http = inject(HttpClient)

  public getAll(entityId: string, searchOptions?: ActivitySearchOptions) {

    this.http.get<Activity[]>(`${this.api}/${entityId}`, {
      params: {
        restrictSearchToEntityId: searchOptions?.restrictSearchToEntityId?.toString() ?? 'false',
        restrictSearchToEntityType: searchOptions?.restrictSearchToEntityType?.toString() ?? 'false',
        restrictSearchToEntityTypeAndId: searchOptions?.restrictSearchToEntityTypeAndId?.toString() ?? 'false',
        entityName: searchOptions?.entityName ?? null
      }
    })
      .pipe(
        tap(x => this.$activities.set([])),
        map(x => Enumerable.from(x).where(a => a.kind !== ActivityKind.File).toArray()),
        map(x => {
          Enumerable
            .from(x)
            .selectMany(a => a.attachments)
            .where(a => a.type === 'File' || a.type === 'Form')
            .forEach(a => {
              a.thumbnail = getFileThumbnailUrl(a);
            });
          return x;
        }),
        catchError(error => {
          return of([] as Activity[]);
        }),
        tap(x => {
          setTimeout(() => {
            this.$updatedActivity.set(null);
            this.$activities.set(x);
          })
        })
      )
      .subscribe();

  }

  public getById(activityId: string): Observable<Activity> {

    return this.http.get<Activity>(`${this.api}/details/${activityId}`)
      .pipe(
        map(x => {

          Enumerable
            .from(x?.attachments)
            .where(a => a.type === 'File' || a.type === 'Form')
            .forEach(a => {
              a.thumbnail = getFileThumbnailUrl(a);
            });

          return x;
        }),
        catchError(error => {
          return of(null);
        })
      )

  }

  public save(activity: Activity): Observable<Activity> {

    return this.http
      .post<Activity>(this.api, activity)
      .pipe(
        tap(x => this.updateActivity(x))
      );
  }

  public share(request: ShareableEntity, refresh = true): void {

    this.http
      .post<Activity>(`${this.api}/share`, request)
      .pipe(
        tap(x => {
          this.updateActivity(x, refresh);
        }),
        catchError(err => {
          console.log(err);
          return of(<Activity>{});
        })
      )
      .subscribe();
  }

  public delete(activityId: string): void {

    this.http
      .delete<ItemDeletedResponse>(`${this.api}/${activityId}`)
      .pipe(
        tap(res => {
          if (res.success) {
            const activities = this.$activities();
            this.$activities.set(activities.filter(x => x._id !== activityId));
            this.$activityDeleted.set(activityId);
          }
        }),
        catchError(err => {
          return of({ _id: activityId, success: false });
        })
      )
      .subscribe();
  }

  protected updateActivity(activity: Activity, updateActivities = true): void {

    const activities = this.$activities();
    const index = activities.findIndex(a => a._id === activity._id);

    if (index >= 0) {

      const old = activities[index];
      activities[index] = { ...old, ...activity };

      if (updateActivities) {
        this.$activities.set([...activities]);
      }

    } else {
      this.$activities.set([...activities, activity]);
      this.$activityAdded.set([activity]);
    }

    this.$updatedActivity.set(activity);

  }

  public updateTags(request: Partial<Activity>, refresh = true): void {

    this.http
      .post<Activity>(`${this.api}/tags`, request)
      .pipe(
        tap(x => {
          this.updateActivity(x, refresh)
        }),
        catchError(err => {
          console.log(err);
          return of(<Activity>{});
        })
      )
      .subscribe();
  }

  public getAttachmentFileName(activityId: string, entity: string): Observable<string> {
    return this.http
      .get<{ name: string }>(`${this.api}/name/${activityId}/${entity}`)
      .pipe(
        map(x => {
          return x?.name;
        }),
        catchError(err => {
          return of(null);
        })
      );
  }

  /**
   * Get last modified date of thumbnail from url
   * @param attachment Attachment that need to check for latest thumbnail
   * @returns Last modified in the header of thumbnail response
   */
  public fetchThumbnailHeader(attachment: Attachment): Observable<string> {
    const latest = {
      ...attachment,
      updatedAt: new Date(),
    } as Attachment;

    const url = getFileThumbnailUrl(latest, true);

    return this.http
      .head(url, { observe: 'response' })
      .pipe(
        map(response => response.headers.get('Last-Modified')),
        catchError(err => {
          return of(null);
        })
      );
  }

  /**
   * Refresh the activity by id
   * @param activityId Activity id to refresh
   */
  public refreshActivity(activityId: string): void {
    this.getById(activityId)
      .pipe(
        tap(x => {
          this.updateActivity(x, false);
        }),
        catchError(err => {
          return of(null);
        })
      )
      .subscribe();
  }
}
